import React, { useEffect, useState } from "react";
import { Download, Mail } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminSubscribers() {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/subscribers")
            .then((r) => setSubs(r.data))
            .finally(() => setLoading(false));
    }, []);

    const downloadCsv = async () => {
        const { data } = await api.get("/admin/subscribers.csv", {
            responseType: "text",
        });
        const blob = new Blob([data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "resham-drops-subscribers.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-subscribers" className="space-y-6">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                        Subscribers
                    </h1>
                    <p className="text-sm text-forest-900/70 mt-1">
                        Customers who opted in for Resham Drops updates at checkout.
                    </p>
                </div>
                <button
                    onClick={downloadCsv}
                    disabled={!subs.length}
                    data-testid="download-csv"
                    className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 disabled:opacity-60 text-gold-400 font-bold text-sm px-5 py-2.5 rounded-xl border border-gold-500/40"
                >
                    <Download className="w-4 h-4" />
                    Export CSV
                </button>
            </header>

            <div className="bg-forest-50 border border-forest-100 rounded-2xl p-4 text-xs text-forest-900/70">
                Use this list with your dedicated marketing tool (Mailchimp, Beehiiv, ConvertKit…) to send campaigns. Transactional emails fire automatically — see <b>Settings</b>.
            </div>

            <div className="bg-white rounded-2xl border border-forest-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-forest-50 text-forest-800 text-left text-[11px] uppercase tracking-widest">
                            <tr>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Phone</th>
                                <th className="px-4 py-3">City</th>
                                <th className="px-4 py-3">Subscribed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-forest-700/60">
                                        Loading…
                                    </td>
                                </tr>
                            )}
                            {!loading && subs.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-forest-700/60">
                                        No subscribers yet. Opt-ins happen on the checkout popup.
                                    </td>
                                </tr>
                            )}
                            {subs.map((s) => (
                                <tr
                                    key={s.email}
                                    data-testid={`subscriber-${s.email}`}
                                    className="border-t border-forest-50 hover:bg-forest-50/40"
                                >
                                    <td className="px-4 py-3 font-semibold text-forest-800 flex items-center gap-2">
                                        <Mail className="w-3.5 h-3.5 text-gold-600" />
                                        {s.email}
                                    </td>
                                    <td className="px-4 py-3">{s.customer_name || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-forest-700/80">{s.phone || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-forest-700/80">{s.city || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-forest-700/60">
                                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
